import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateForTask } from "@/lib/useTaskMutations";
import { Icon } from "./Icon";

type Tag = { id: string; name: string; color: string };

const PALETTE = [
  "#3b82d4",
  "#1f8a5b",
  "#e8612c",
  "#dc3b32",
  "#7c3aed",
  "#0ea5e9",
  "#f59e0b",
  "#94a0b4",
];

/**
 * TagPicker — designs/TaskDetail.html `.tag-input` block.
 *
 * Renders the assigned tags as chips with an inline `+ Tag` button that
 * opens a popover. The popover lists existing tags (toggle-to-assign) and
 * a search input that doubles as a create-new field — typing a name not in
 * the list and pressing Enter creates the tag with the next palette colour
 * and assigns it in one step.
 */
export function TagPicker({
  taskId,
  projectId,
  taskTags,
}: {
  taskId: string;
  projectId: string;
  taskTags: Tag[];
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { data } = useQuery({
    queryKey: queryKeys.tags(),
    queryFn: api.listTags,
  });
  const assign = useMutation({
    mutationFn: (tagId: string) => api.assignTag(taskId, tagId),
    onSuccess: () => invalidateForTask(qc, { projectId, taskId }),
  });
  const unassign = useMutation({
    mutationFn: (tagId: string) => api.unassignTag(taskId, tagId),
    onSuccess: () => invalidateForTask(qc, { projectId, taskId }),
  });
  const create = useMutation({
    mutationFn: async (name: string) => {
      const color = PALETTE[(data?.tags?.length ?? 0) % PALETTE.length]!;
      const t = await api.createTag({ name, color });
      await api.assignTag(taskId, t.tag.id);
      return t.tag;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tags() });
      invalidateForTask(qc, { projectId, taskId });
    },
  });

  const assignedIds = new Set(taskTags.map((t) => t.id));
  const allTags = data?.tags ?? [];

  const q = query.trim().toLowerCase();
  const filtered = q
    ? allTags.filter((t) => t.name.toLowerCase().includes(q))
    : allTags;
  const exactMatch = q ? allTags.some((t) => t.name.toLowerCase() === q) : true;

  // Close on click-outside.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  return (
    <div className="tag-picker" ref={rootRef}>
      <div className="tag-input">
        {taskTags.map((t) => (
          <span key={t.id} className="tag-chip">
            <span className="d" style={{ background: t.color }} />
            {t.name}
            <button
              type="button"
              className="rm"
              aria-label={`Remove tag ${t.name}`}
              onClick={() => unassign.mutate(t.id)}
            >
              <Icon name="close" size={12} />
            </button>
          </span>
        ))}
        <button
          type="button"
          className="btn btn-secondary btn-sm tag-add-btn"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <Icon name="plus" size={12} />
          Tag
        </button>
      </div>
      {open && (
        <div className="tag-picker-panel" role="listbox">
          <input
            ref={inputRef}
            className="tag-picker-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search or create…"
            aria-label="Search tags"
            onKeyDown={(e) => {
              if (e.key === "Enter" && q && !exactMatch) {
                e.preventDefault();
                create.mutate(query.trim());
                setQuery("");
                setOpen(false);
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
          />
          <div className="tag-picker-list">
            {filtered.map((t) => {
              const on = assignedIds.has(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={`tag-picker-row${on ? " on" : ""}`}
                  onClick={() => {
                    if (on) unassign.mutate(t.id);
                    else assign.mutate(t.id);
                    setOpen(false);
                  }}
                >
                  <span className="d" style={{ background: t.color }} />
                  {t.name}
                  {on && (
                    <span className="check" aria-hidden>
                      <Icon name="check" size={12} />
                    </span>
                  )}
                </button>
              );
            })}
            {q && !exactMatch && (
              <button
                type="button"
                className="tag-picker-row tag-picker-create"
                onClick={() => {
                  create.mutate(query.trim());
                  setQuery("");
                  setOpen(false);
                }}
              >
                <Icon name="plus" size={12} />
                Create "{query.trim()}"
              </button>
            )}
            {filtered.length === 0 && !q && (
              <div className="tag-picker-empty t-xs t-muted">
                No tags yet. Type to create one.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
